import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import {
    ItemCard,
    type PossibleItemTypes
} from 'tradera/components/item-card/item-card';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { trackContainerClick } from '../analytics';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import styles from './wish-list.module.scss';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import {
    endpoints,
    useGetStartPageWishListItemsQuery
} from 'tradera/state/services/discover-api';

const MAX_DISPLAYED_COUNT = 50;
const emptyArray: never[] = [];

const WishListRtkq = () => {
    const isLoggedIn = useIsLoggedIn();
    const isWishListEnabled = useIsFeatureEnabled('startpage-wish-list');

    return isLoggedIn && isWishListEnabled ? <WishListInternal /> : null;
};

const WishListInternal = () => {
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    const { currentData } =
        endpoints.getStartPageWishListItems.useQueryState(undefined);
    const totalCount = currentData?.totalCount ?? 0;
    const items = currentData?.items ?? emptyArray;

    useGetStartPageWishListItemsQuery(undefined, {
        // Skip if we already have data or trigger out of view
        skip: !!currentData || !loadingTriggerInView
    });

    const displayItems = useMemo(
        () => items.slice(0, MAX_DISPLAYED_COUNT),
        [items]
    );
    const moreItemsExist =
        Math.min(items.length, MAX_DISPLAYED_COUNT) < totalCount;

    const toggleWishList = useCallback(
        (itemId: number) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch<any>(itemActions.wishlist(itemId));
        },
        [dispatch]
    );

    const openQuickView = useCallback(
        (item: PossibleItemTypes) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch<any>(
                uiActions.openQuickViewAction({
                    items,
                    item,
                    openedFrom: 'Start Page Wish List'
                })
            ),
        [dispatch, items]
    );

    const { toLocalizedUrl } = useUrlLocalizer();

    const showSection = displayItems.length > 0;

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {!showSection ? null : (
                <>
                    <StartPageSectionTitle
                        href={toLocalizedUrl('/my/wish-list')}
                        inSpa={false} // Use 'inSPA' when mytradera-page can be navigated to without a hard reload
                        onClick={() =>
                            trackContainerClick(
                                'wishList',
                                'Wish-List button below'
                            )
                        }
                        showAll>
                        {t('myt_wishlist_title')}
                    </StartPageSectionTitle>
                    <Carousel
                        id="wish-list"
                        numberItemsToScroll={numberCarouselCards}
                        numberItemsToShow={numberCarouselCards}
                        padX
                        className="mx-md-3 mx-lg-2 mx-xl-0">
                        <LeftButton size="md" alignVerticallyToItemCards />
                        <CarouselContent>
                            {displayItems.map((item) => (
                                <div
                                    key={item.itemId}
                                    className={`${styles.wishListItem}`}>
                                    <ItemCard
                                        // This is to make the parent container set grid gaps instead
                                        // of individual cards keeping paddings and margins set of their own.
                                        // Makes displaying cards in different situations more predictable.
                                        className="m-0 p-0"
                                        item={item}
                                        onAddToWishlist={toggleWishList}
                                        onQuickView={openQuickView}
                                        onRemoveFromWishlist={toggleWishList}
                                        gaCategory="Startpage wish list"
                                        showBidStatus
                                        showTitleOnOneLine
                                        showShippingOrOnDisplay={false}
                                    />
                                </div>
                            ))}
                            {moreItemsExist ? (
                                <MoreResultsCard
                                    href={toLocalizedUrl('/my/wish-list')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'wishList',
                                            'Wish-List button below'
                                        )
                                    }
                                    label={t('myt_to_wishlist_link')}
                                />
                            ) : null}
                        </CarouselContent>
                        <RightButton size="md" alignVerticallyToItemCards />
                    </Carousel>
                </>
            )}
        </StartPageSection>
    );
};

export default WishListRtkq;
